import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'
import compact from 'lodash/compact'
import xor from 'lodash/xor'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import UserSelector from 'modules/core/components/UserSelector'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import type { Users } from 'modules/core/store/collections/users'
import type AccessGroup from 'modules/core/store/models/AccessGroup'

import type { Employees } from 'modules/employees/store/collections/employees'
import { useEngagementContext } from 'modules/engagement/hooks/useEngagementContext'
import { computeAvailableUsers } from 'modules/surveys/components/New/AccessGroupPicker/ManualSelection'
import { SurveyParticipantsContext } from 'modules/surveys/pages/Show/Participants/hooks'
import questionGroups from 'modules/surveys/store/collections/questionGroups'

type UserSelectorProps = React.ComponentProps<typeof UserSelector>

interface Props {
  accessGroup: AccessGroup
  surveyId: number
  back: string
  users: Users
  employees: Employees
}

function DynamicNewRespondent({ surveyId, accessGroup, back, users, employees }: Props) {
  const { allMyDescendants } = useCurrentSession()
  const navigate = useNavigate()
  const { fetchRespondents } = React.useContext(SurveyParticipantsContext)
  const [userIds, setUserIds] = React.useState<number[]>([])
  const [running, setRunning] = React.useState(false)
  const { canCreateEverybody, canCreateReportees, canCreateTeam, canCreateOwned } =
    useEngagementContext()
  const onSelectMultiple: UserSelectorProps['onSelectMultiple'] = (selectedIds) =>
    setUserIds(selectedIds)
  const memberIds = accessGroup.attributes.access_ids
  const {
    access: { id: userId },
  } = useCurrentSession()
  const availableUsers = React.useRef(
    computeAvailableUsers(
      users,
      employees,
      userId,
      false,
      allMyDescendants,
      canCreateEverybody,
      canCreateReportees,
      canCreateTeam,
      canCreateOwned
    )
  ).current

  const notMembers = availableUsers.filter((u) => !memberIds.includes(u.get('id')))

  const onClose = () => navigate(back)

  const submit = async (closePortal: ClosePortalFn) => {
    setRunning(true)

    const { success } = await asyncAction(() =>
      questionGroups.bulkCreate({
        form_id: surveyId,
        accesses_ids: userIds,
      })
    )
    setRunning(false)

    if (success) {
      closePortal()
      fetchRespondents && fetchRespondents()
    }
  }

  return (
    <Modal
      title={i18n.t('surveys.form.participants.new.title')}
      description={i18n.t('surveys.form.participants.new.description.survey')}
      illustration="surveys"
      onClose={onClose}
      onSubmit={submit}
      footer={
        <Button disabled={!userIds.length || running} submit>
          {i18n.t('surveys.form.participants.new.submit')}
        </Button>
      }
    >
      <UserSelector
        users={notMembers}
        onSelect={(id) => setUserIds(xor(userIds, compact([id])))}
        onSelectMultiple={onSelectMultiple}
        selectedIds={userIds}
      />
    </Modal>
  )
}

export default observer(DynamicNewRespondent)
